
import { defineComponent, PropType, h } from 'vue';
import { NDropdown, NCheckbox, useNotification } from 'naive-ui';
import moment from 'moment';
import { mapStores, mapState } from 'pinia';

import { Station } from '~/api/models/station.model';

import ComponentBase from '~/components/component-base';
import ModalConfirm from '~/components/modals/modal-confirm.vue';
import StationExportModal from '~/components/modals/station-export-modal.vue';
import StationEditionModal from '~/components/modals/station-edition-modal.vue';

import { useStationStore } from '~/store/station.store';
import { useAuthStore } from '~/store/auth.store';

import { Block } from 'notiflix';
import { BadRequestError } from '~/api/exceptions/bad-request.error';

enum DropdownOptions {
    EXPORT = 'EXPORT',
    FORGET = 'FORGET',
    EDIT = 'EDIT'
}



export default defineComponent({

    components: { NDropdown, ModalConfirm, StationExportModal, NCheckbox, StationEditionModal },

    $refs: {
        modalConfirm: ModalConfirm,
        exportModal: StationExportModal,
        editStationModal: StationEditionModal,
    },

    props: {
        station: {
            type: Object as PropType<Station>,
            required: true
        },
        active: {
            type: Boolean,
            default: false,
        },
        forgettable: {
            type: Boolean,
            default: true,
        },
    },

    extends: ComponentBase,

    data() {
        return {
            moment: moment,
            notifications: useNotification(),
            showExportModal: false,
            loadEditModal: true,
            show: false,
            options: [
                {
                    label: this.$t('station_list.export.title'),
                    key: DropdownOptions.EXPORT,
                },
                {
                    label: this.$t('station_list.forget.title'),
                    key: DropdownOptions.FORGET,
                },
                {
                    label: this.$t('station_list.edit.title'),
                    key: DropdownOptions.EDIT,
                }
            ].filter(o => o.key !== DropdownOptions.FORGET || o.key === DropdownOptions.FORGET && this.forgettable),
        };
    },

    computed: {
        ...mapStores(useStationStore, useAuthStore),
        ...mapState(useAuthStore, ['user', 'favorites']),

        isActive() {
            return this.active;
        }
    },

    methods: {

        handleDropdownButtonClick($event: Event): void {
            $event.stopPropagation();
            this.show = !this.show;
        },

        async forgetStation(station: Station): Promise<void> {
            const result = await this.$confirm(this.$t('station_list.forget.title'), this.$t('station_list.forget.question'));

            if (!result) {
                return;
            }

            try {
                Block.dots([this.$refs.wrapper as HTMLElement]);
                await this.stationStore.forgetStation(station);
                this.$emit('station:forget', station)
                this.cleanQuery();
                this.success(this.$t('station_list.forget.success_message'),
                );
            } catch (e) {
                this.showUnhandledError(`Station ${this.getStationName(station)} could not be forgotten`, e);
            } finally {
                Block.remove([this.$refs.wrapper as HTMLElement])
            }

        },

        async editStation(station: Station) {
            this.loadEditModal = true;
            //@ts-ignore
            this.$refs.stationEditionModal.editStation(station)
        },

        renderLabel(option) {
            switch (option.key) {
                case DropdownOptions.EXPORT:
                    return h('span', { class: 'roboto has-text-black' }, [this.$t('station_list.export.title')]);
                case DropdownOptions.FORGET:
                    return h('span', { class: 'roboto has-text-danger' }, [this.$t('station_list.forget.title')]);
                 case DropdownOptions.EDIT:
                    return h('span', { class: 'roboto has-text-black' }, [this.$t('station_list.edit.title')]);
            }
        },

        renderDropdownIcon(option) {
            switch (option.key) {
                case DropdownOptions.EXPORT:
                    return h('span', { class: 'mdi mdi-upload has-text-black' }, []);
                case DropdownOptions.FORGET:
                    return h('span', { class: 'mdi mdi-delete has-text-danger' }, []);
                case DropdownOptions.EDIT:
                    return h('span', { class: 'mdi mdi-pencil has-text-black' }, []);
            }
        },

        async handleSelect(key: DropdownOptions, station: Station) {
            switch (key) {
                case DropdownOptions.EXPORT:
                    this.$refs.exportModal.open();
                    break;
                case DropdownOptions.FORGET:
                    await this.forgetStation(station);
                    break;
                case DropdownOptions.EDIT:
                    await this.editStation(station);
                    break;
            }
        },

        async addFavoriteStation($event, station: Station): Promise<void> {
            try {
                $event.preventDefault();
                $event.stopPropagation();
                await this.$authService.addUserFavorite(station);
                await this.authStore.fetchUser(true);
            } catch(e) {
                if(e instanceof BadRequestError) {
                    this.notification(
                        'warning',
                        this.$t('station_list.favorite.failed'),
                        this.$t('station_list.favorite.station_not_geolocalized'),
                        { 
                            label: this.$t('station_list.edit.title'), 
                            callback: () => this.editStation(station)
                        }
                    )
                } else {
                    this.showUnhandledError('Could not add favorite', e);
                }
            }
        },

        async removeFavoriteStation($event, station: Station): Promise<void> {
            try {
                $event.preventDefault();
                $event.stopPropagation();
                await this.$authService.removeUserFavorite(station);
                await this.authStore.fetchUser(true);
            } catch(e) {
                this.showUnhandledError('Could not remove favorite', e);
            }

        }
    },
})
